import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { Star, StarHalf } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StarRating = ({ rating, onRate, interactive = false, size = 20 }) => {
  const [hoverRating, setHoverRating] = useState(0);

  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={!interactive}
          onClick={() => interactive && onRate(star)}
          onMouseEnter={() => interactive && setHoverRating(star)}
          onMouseLeave={() => interactive && setHoverRating(0)}
          className={`transition-colors ${interactive ? 'cursor-pointer' : 'cursor-default'}`}
        >
          <Star
            size={size}
            className={`${star <= (hoverRating || rating)
                ? 'fill-yellow-500 text-yellow-500'
                : 'text-zinc-600'
              }`}
          />
        </button>
      ))}
    </div>
  );
};

const ReviewSection = ({ artistId, artistName }) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newReview, setNewReview] = useState({ rating: 0, comment: '' });
  const [submitting, setSubmitting] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);

  useEffect(() => {
    fetchReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artistId]);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/artists/${artistId}/reviews`);
      setReviews(response.data);
      // Check if current user has already reviewed
      if (user) {
        setHasReviewed(response.data.some(r => r.client_id === user.id));
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async () => {
    if (newReview.rating === 0) {
      toast.error('Por favor selecciona una calificación');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/artists/${artistId}/reviews`, newReview);
      setReviews(prev => [response.data, ...prev]);
      setDialogOpen(false);
      setNewReview({ rating: 0, comment: '' });
      setHasReviewed(true);
      toast.success('¡Reseña publicada exitosamente!');
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error(error.response?.data?.detail || 'Error al publicar la reseña');
    } finally {
      setSubmitting(false);
    }
  };

  const averageRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  return (
    <div className="mt-8" data-testid="reviews-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-['Cinzel'] text-xl font-semibold text-white">Reseñas</h3>
          <div className="flex items-center gap-2 mt-1">
            <StarRating rating={Math.round(averageRating)} size={16} />
            <span className="text-sm text-zinc-400">
              {averageRating} ({reviews.length} reseña{reviews.length !== 1 ? 's' : ''})
            </span>
          </div>
        </div>

        {user?.user_type === 'client' && !hasReviewed && user?.id !== artistId && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                className="border-zinc-700 text-zinc-300 hover:border-zinc-300 hover:text-white bg-transparent uppercase tracking-widest text-xs"
                data-testid="write-review-btn"
              >
                Escribir Reseña
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="font-['Cinzel'] text-xl">
                  Reseña para {artistName}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-6 mt-4">
                <div>
                  <p className="text-sm text-zinc-500 mb-3">Tu calificación</p>
                  <StarRating
                    rating={newReview.rating}
                    onRate={(r) => setNewReview(prev => ({ ...prev, rating: r }))}
                    interactive
                    size={32}
                  />
                </div>
                <div>
                  <p className="text-sm text-zinc-500 mb-3">Tu comentario (opcional)</p>
                  <Textarea
                    value={newReview.comment}
                    onChange={(e) => setNewReview(prev => ({ ...prev, comment: e.target.value }))}
                    placeholder="Comparte tu experiencia..."
                    className="bg-transparent border-zinc-800 min-h-[100px]"
                    data-testid="review-comment"
                  />
                </div>
                <Button
                  onClick={handleSubmitReview}
                  disabled={submitting || newReview.rating === 0}
                  className="w-full bg-zinc-100 text-zinc-950 hover:bg-white uppercase tracking-widest text-xs font-bold py-4"
                  data-testid="submit-review-btn"
                >
                  {submitting ? 'Publicando...' : 'Publicar Reseña'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Reviews List */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-zinc-900/50 border border-zinc-800 p-6 animate-pulse">
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-zinc-800" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-24 bg-zinc-800" />
                  <div className="h-3 w-full bg-zinc-800" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900/30 border border-zinc-800">
          <Star className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500">Aún no hay reseñas</p>
          {user?.user_type === 'client' && !hasReviewed && (
            <p className="text-sm text-zinc-600 mt-2">¡Sé el primero en dejar una reseña!</p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <div
              key={review.id}
              className="bg-zinc-900/50 border border-zinc-800 p-6"
              data-testid={`review-${review.id}`}
            >
              <div className="flex gap-4">
                <Avatar className="h-10 w-10 border border-zinc-800">
                  <AvatarImage src={review.client_avatar} />
                  <AvatarFallback className="bg-zinc-800 text-zinc-400 text-sm">
                    {review.client_name?.charAt(0)?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-white">{review.client_name}</p>
                    <span className="text-xs text-zinc-600">
                      {formatDistanceToNow(new Date(review.created_at), { addSuffix: true, locale: es })}
                    </span>
                  </div>
                  <StarRating rating={review.rating} size={14} />
                  {review.comment && (
                    <p className="text-zinc-400 mt-3">{review.comment}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export { ReviewSection, StarRating };
export default ReviewSection;
